// Edit-mode tray: the scheduler's control strip while staging draft changes.
//
// Factual readouts only — the original mock's pool-tier breakdown (dedicated /
// borrowed / temps "free vs needed") was dropped with the derived capacity
// ladder; composition becomes tracked data in a later phase. What survives is
// what the scheduler actually needs while allocating: the window's peak day,
// how many unpublished changes are staged, and the Publish / Reset / Done
// controls.

import { fmtDateTime } from '@/lib/dates';

export default function AssignmentTray({
  peakDay, peakFab, peakInstall, fabScope,
  draftCount, lastDraftAt, othersDraftCount, otherDraftEditors, busy,
  onPublish, onReset, onDone, children,
}: {
  /** Max company (or fab-scope) crew on any single day in the window. */
  peakDay: number;
  /** Fab lens: peak fabrication-phase / installation-phase crew per day. */
  peakFab?: number;
  peakInstall?: number;
  fabScope: boolean;
  /** YOUR staged cells — exactly what Publish will fold in. */
  draftCount: number;
  lastDraftAt: string | null;
  /** Cells another scheduler has staged. Visible here, but not yours to publish. */
  othersDraftCount: number;
  otherDraftEditors: string[];
  busy: boolean;
  onPublish: () => void;
  onReset: () => void;
  onDone: () => void;
  children?: React.ReactNode;
}) {
  const dirty = draftCount > 0;
  // Someone else is mid-edit. Publish and Reset only ever touch your own cells,
  // but you need to know their work exists — otherwise two schedulers overwrite
  // each other's days without either realising.
  const others = othersDraftCount > 0;
  const otherNames = otherDraftEditors.join(', ');
  return (
    // mp-noprint: this whole tray is edit-mode scaffolding — the keystroke
    // instructions, the draft count and the Publish/Reset controls. On paper it
    // is a bar of directions for a screen the reader is not looking at. The
    // fact that the printout came from an unpublished draft still reaches them,
    // through the UNPUBLISHED DRAFT line in the print header.
    <div className="mp-noprint" style={{
      position: 'sticky', top: 0, zIndex: 30,
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      padding: '8px 14px', marginBottom: 12,
      background: 'var(--ew-green-50)', border: '1px solid var(--ew-green-100)',
      borderLeft: '3px solid var(--ew-green)', borderRadius: 'var(--ew-r-md)',
      boxShadow: 'var(--ew-shadow-2)',
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ew-green-700)' }}>
        ✎ Edit mode
      </span>
      {/* The tray is sticky, so on a narrow window its instructions would cost
          two or three lines of grid for the whole session. They shorten to the
          one thing worth repeating; the rest is in ? Help. */}
      <style>{`
        .mp-tray-short { display: none; }
        @media (max-width: 1180px) {
          .mp-tray-long { display: none; }
          .mp-tray-short { display: inline; }
        }
      `}</style>
      <span style={{ fontSize: 12, color: 'var(--ew-n-500)' }}>
        <span className="mp-tray-long">Type into day cells · drag or shift-click to select several · set a row with − / + · shift a job with ◀ ▶ — changes stage as a draft PMs can't see until you publish.</span>
        <span className="mp-tray-short">Changes stay a draft until you publish.</span>
      </span>

      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--ew-n-500)', fontVariantNumeric: 'tabular-nums' }}>
          Peak day <b style={{ color: 'var(--ew-dark)' }}>{peakDay}</b>
          {fabScope && peakFab != null && peakInstall != null ? (
            <span style={{ marginLeft: 6, color: 'var(--ew-n-400)' }}>
              (<b style={{ color: 'var(--ew-dark)' }}>{peakFab}</b> shop · <b style={{ color: 'var(--ew-quality)' }}>{peakInstall}</b> field)
            </span>
          ) : null}
        </span>

        <span
          aria-live="polite"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', borderRadius: 'var(--ew-r-pill)',
            fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
            background: dirty ? 'var(--ew-performance-50)' : 'var(--ew-white)',
            color: dirty ? 'var(--ew-performance)' : 'var(--ew-n-400)',
            border: `1px solid ${dirty ? '#fed7aa' : 'var(--ew-n-200)'}`,
          }}
          title={lastDraftAt ? `Last edit ${fmtDateTime(lastDraftAt)}` : undefined}
        >
          {dirty ? `● ${draftCount} unpublished change${draftCount === 1 ? '' : 's'}` : 'No unpublished changes'}
        </span>

        {others && (
          <span
            aria-live="polite"
            title={`${otherNames} ${otherDraftEditors.length === 1 ? 'has' : 'have'} staged changes that are not published yet. Publish and Reset only affect your own edits — theirs are left alone.`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', borderRadius: 'var(--ew-r-pill)',
              fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
              background: 'var(--ew-quality-50, #eff6ff)', color: 'var(--ew-quality, #1d4ed8)',
              border: '1px solid #bfdbfe',
            }}
          >
            👥 {othersDraftCount} staged by {otherNames || 'another scheduler'}
          </span>
        )}

        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px', color: 'var(--ew-danger)' }} disabled={!dirty || busy}
          title="Discards only the changes you staged"
          onClick={() => { if (confirm(`Discard your ${draftCount} unpublished change${draftCount === 1 ? '' : 's'}?${others ? `\n\n${otherNames}'s staged changes are not affected.` : ''}`)) onReset(); }}>
          Reset changes
        </button>
        <button className="btn btn-primary" style={{ fontSize: 12, padding: '5px 14px' }} disabled={!dirty || busy}
          title={others
            ? `Publishes only your ${draftCount} change${draftCount === 1 ? '' : 's'} — ${otherNames}'s staged work stays unpublished`
            : 'Make your staged changes live for everyone and exit edit mode'}
          onClick={onPublish}>
          {busy ? 'Working…' : `Publish${dirty ? ` (${draftCount})` : ''}`}
        </button>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }} disabled={busy}
          title={dirty ? 'Exit edit mode without publishing — your draft stays staged for later' : 'Exit edit mode'}
          onClick={onDone}>
          {dirty ? 'Close (keep draft)' : 'Done'}
        </button>
      </span>
      {/* The multi-cell selection bar (#589) rides in the tray so it stays
          pinned with it — below, it scrolled away under the sticky tray. */}
      {/* An inline-size container, so the bar compacts on ITS width. */}
      {children && <div style={{ flexBasis: '100%', containerType: 'inline-size' }}>{children}</div>}
    </div>
  );
}
